import { formatActor, formatUtcDateTime } from './diffViewerFormatting';
import { positiveIntegerPattern } from './diffViewerQuery';

export const maxParentPbiId = 2147483647;

const notAvailableText = "—";

export const controlReadFailedText = "The comparison control could not be loaded.";
export const waitNoticeText = "Clear down, and a Start for a different PBI, are refused within 1 minute of a stop.";
export const runningText = "The comparison is running.";
export const configurationOffText = "Configuration has the comparison switched off.";
export const notRecordingText = "No UnderTest differences are being recorded while configuration has the comparison switched off.";
export const recordedBeforeControlText = "These UnderTest results were recorded before the comparison control existed. Clear them down before starting a comparison.";
export const stopBeforeClearDownText = "Stop the comparison before clearing it down.";
export const nothingToClearDownText = "There is nothing to clear down.";
export const parentPbiRequiredText = "A parent PBI is required to start the comparison.";
export const parentPbiInvalidText = "The parent PBI must be a whole number greater than 0.";
export const parentPbiGuidanceText = "Enter the parent PBI as a whole number greater than 0.";
export const personalDataText = "Retained requests hold driver personal data.";
export const phaseOneKeptText = "Phase 1 (Legacy v RE2) results are kept.";

export function buildResumeText(parentPbiId) {
    return `Results from PBI ${parentPbiId} are still recorded. Only PBI ${parentPbiId} can be started; to start a different PBI, clear down first.`;
}

function pluralise(count, singular, plural) {
    return `${count} ${count === 1 ? singular : plural}`;
}

export function formatRecordedCounts({ differences, signOffs, retainedRequests }) {
    return `${pluralise(differences, "UnderTest difference", "UnderTest differences")}, ${pluralise(signOffs, "sign-off", "sign-offs")} and ${pluralise(retainedRequests, "retained request", "retained requests")}`;
}

export function formatClearDownWarning(counts) {
    return `${formatRecordedCounts(counts)} will be removed.`;
}

export function formatClearDownResult(counts) {
    return `Removed ${formatRecordedCounts(counts)}.`;
}

function formatControlStatus(control) {
    if (control.running) {
        return "Running";
    }

    return "Stopped";
}

function formatControlConfiguration(control) {
    if (control.configurationEnabled) {
        return "Switched on";
    }

    return "Switched off";
}

// Who last started or stopped the comparison, and when, in UTC.
function formatControlEvent(displayName, userId, at) {
    if (at == null) {
        return notAvailableText;
    }

    return `${formatActor(displayName, userId)} at ${formatUtcDateTime(at)} (UTC)`;
}

// The read-only state fields; every value is the not-available text until the control has been read.
export function buildControlFields(control) {
    const loaded = control != null;

    return [
        { key: "status", header: "Status", value: loaded ? formatControlStatus(control) : notAvailableText },
        { key: "configuration", header: "Configuration", value: loaded ? formatControlConfiguration(control) : notAvailableText },
        { key: "parent-pbi", header: "Parent PBI", value: control?.parentPbiId ?? notAvailableText },
        { key: "started", header: "Started", value: formatControlEvent(control?.startedByDisplayName, control?.startedByUserId, control?.startedAt) },
        { key: "stopped", header: "Stopped", value: formatControlEvent(control?.stoppedByDisplayName, control?.stoppedByUserId, control?.stoppedAt) },
        { key: "recorded", header: "Recorded", value: loaded ? formatRecordedCounts(control.counts) : notAvailableText }
    ];
}

export function mapControlResponse(response) {
    return {
        running: response.running === true,
        configurationEnabled: response.configurationEnabled === true,
        parentPbiId: response.parentPbiId ?? null,
        startedByUserId: response.startedByUserId ?? null,
        startedByDisplayName: response.startedByDisplayName ?? null,
        startedAt: response.startedAt ?? null,
        stoppedByUserId: response.stoppedByUserId ?? null,
        stoppedByDisplayName: response.stoppedByDisplayName ?? null,
        stoppedAt: response.stoppedAt ?? null,
        counts: {
            differences: response.differenceCount ?? 0,
            signOffs: response.signOffCount ?? 0,
            retainedRequests: response.retainedRequestCount ?? 0
        }
    };
}

export function mapClearDownResponse(response) {
    return {
        differences: response?.differencesRemoved ?? 0,
        signOffs: response?.signOffsRemoved ?? 0,
        retainedRequests: response?.retainedRequestsRemoved ?? 0
    };
}

export function hasRecordedRows(control) {
    return control.counts.differences > 0 || control.counts.signOffs > 0 || control.counts.retainedRequests > 0;
}

// Rows with no stored PBI can only come from runs before the control existed, and the API refuses every
// Start until they are cleared down.
export function isNeverStartedWithRows(control) {
    return !control.running && control.parentPbiId == null && hasRecordedRows(control);
}

// The API only accepts a Start for the stored PBI while that run's rows remain.
export function isResumable(control) {
    return !control.running && control.parentPbiId != null && hasRecordedRows(control);
}

// The parent PBI box as the control last read describes it; a fresh read replaces whatever was typed.
export function buildParentPbiBox(control) {
    if (control == null) {
        return { value: "", helperText: "", disabled: true };
    }

    if (control.running) {
        return { value: `${control.parentPbiId ?? ""}`, helperText: "", disabled: true };
    }

    if (isNeverStartedWithRows(control)) {
        return { value: "", helperText: "", disabled: true };
    }

    if (isResumable(control)) {
        return { value: `${control.parentPbiId}`, helperText: buildResumeText(control.parentPbiId), disabled: false };
    }

    return { value: "", helperText: parentPbiGuidanceText, disabled: false };
}

export function validateParentPbi(text) {
    const trimmed = text.trim();

    if (trimmed === "") {
        return parentPbiRequiredText;
    }

    if (!positiveIntegerPattern.test(trimmed) || Number(trimmed) > maxParentPbiId) {
        return parentPbiInvalidText;
    }

    return "";
}

// One tooltip, the first reason that applies; a request in flight is not a reason of its own.
export function chooseStartTooltip(control) {
    if (control == null) {
        return null;
    }

    if (control.running) {
        return runningText;
    }

    if (!control.configurationEnabled) {
        return configurationOffText;
    }

    if (isNeverStartedWithRows(control)) {
        return recordedBeforeControlText;
    }

    return null;
}

export function chooseClearDownTooltip(control) {
    if (control == null) {
        return null;
    }

    if (control.running) {
        return stopBeforeClearDownText;
    }

    if (!hasRecordedRows(control)) {
        return nothingToClearDownText;
    }

    return null;
}

export function buildControlStatements(control) {
    if (control == null) {
        return [];
    }

    const statements = [];

    if (!control.configurationEnabled) {
        statements.push(configurationOffText);

        if (control.running) {
            statements.push(notRecordingText);
        }
    }

    if (isNeverStartedWithRows(control)) {
        statements.push(recordedBeforeControlText);
    }

    return statements;
}

// What each action may do, given the last read and whether a request or a refresh is in flight.
export function buildControlActions(control, busy) {
    const startTooltip = chooseStartTooltip(control);
    const clearDownTooltip = chooseClearDownTooltip(control);
    const loaded = control != null;

    return {
        startEnabled: loaded && !busy && startTooltip == null,
        startTooltip: startTooltip,
        stopEnabled: loaded && !busy && control.running,
        clearDownEnabled: loaded && !busy && clearDownTooltip == null,
        clearDownTooltip: clearDownTooltip
    };
}
